import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

import ContactLinksDialog from '../components/ContactLinksDialog';
import { 
  ArrowLeft, Mail, Phone, Linkedin, Building, Building2, MapPin, 
  Star, Edit2, Save, X, Briefcase, Calendar, Link2,
  User, Clock, FileText, Plus, ShieldOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countryFlags = {
  SA: '🇸🇦', AE: '🇦🇪', OM: '🇴🇲', KW: '🇰🇼', BH: '🇧🇭', QA: '🇶🇦'
};

const countryNames = {
  en: { SA: 'Saudi Arabia', AE: 'UAE', OM: 'Oman', KW: 'Kuwait', BH: 'Bahrain', QA: 'Qatar' },
  ar: { SA: 'السعودية', AE: 'الإمارات', OM: 'عمان', KW: 'الكويت', BH: 'البحرين', QA: 'قطر' }
};

const positionColors = {
  ceo: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  director: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  manager: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  engineer: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  consultant: 'bg-pink-500/10 text-pink-500 border-pink-500/30',
  analyst: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30'
};

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { getToken } = useAuth();
  
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [relatedOpportunities, setRelatedOpportunities] = useState([]);
  const [linkOptions, setLinkOptions] = useState({ companies: [], projects: [] });
  const [showLinksDialog, setShowLinksDialog] = useState(false);

  const translations = {
    en: {
      backToContacts: 'Back to Contacts',
      contactDetails: 'Contact Details',
      editContact: 'Edit Contact',
      save: 'Save',
      cancel: 'Cancel',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      position: 'Position',
      company: 'Company',
      country: 'Country',
      city: 'City',
      linkedin: 'LinkedIn',
      notes: 'Notes',
      lastContact: 'Last Contact',
      createdAt: 'Created',
      updatedAt: 'Updated',
      relatedOpportunities: 'Related Opportunities',
      noRelatedItems: 'No related opportunities found',
      source: 'Source',
      industries: 'Industries',
      loading: 'Loading contact...',
      notFound: 'Contact not found',
      saveSuccess: 'Contact updated successfully',
      saveError: 'Failed to update contact'
    },
    ar: {
      backToContacts: 'العودة لجهات الاتصال',
      contactDetails: 'تفاصيل جهة الاتصال',
      editContact: 'تعديل جهة الاتصال',
      save: 'حفظ',
      cancel: 'إلغاء',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      position: 'المنصب',
      company: 'الشركة',
      country: 'الدولة',
      city: 'المدينة',
      linkedin: 'لينكد إن',
      notes: 'ملاحظات',
      lastContact: 'آخر تواصل',
      createdAt: 'تاريخ الإنشاء',
      updatedAt: 'آخر تحديث',
      relatedOpportunities: 'الفرص المرتبطة',
      noRelatedItems: 'لا توجد فرص مرتبطة',
      source: 'المصدر',
      industries: 'القطاعات',
      loading: 'جاري تحميل جهة الاتصال...',
      notFound: 'جهة الاتصال غير موجودة',
      saveSuccess: 'تم تحديث جهة الاتصال بنجاح',
      saveError: 'فشل تحديث جهة الاتصال'
    }
  };

  const txt = translations[language];

  const fetchContact = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/contacts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(txt.notFound);
        } else {
          throw new Error('Failed to fetch contact');
        }
        return;
      }
      
      const data = await response.json();
      setContact(data);
      setEditData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        position: data.position || '',
        company: data.company || '',
        country: data.country || '',
        city: data.city || '',
        linkedin: data.linkedin || '',
        notes: data.notes || ''
      });
      
      // Fetch related opportunities if contact has source_id
      if (data.source_id) {
        fetchRelatedOpportunities(data.source_id);
      }
    } catch (err) {
      console.error('Error fetching contact:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedOpportunities = async (sourceId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/opportunities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // API returns a paginated object {items:[...]} or a plain array
        const list = Array.isArray(data) ? data : (data.items || []);
        // Filter opportunities that match the source_id or contact's company
        const related = list.filter(opp => 
          opp._id === sourceId || 
          opp.name?.toLowerCase().includes(contact?.company?.toLowerCase() || '')
        );
        setRelatedOpportunities(related.slice(0, 5));
      }
    } catch (err) {
      console.error('Error fetching related opportunities:', err);
    }
  };

  useEffect(() => {
    if (id) {
      fetchContact();
    }
  }, [id]);

  useEffect(() => {
    const fetchLinkOptions = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/contacts/linkable-entities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setLinkOptions({ companies: data.companies || [], projects: data.projects || [] });
        }
      } catch (e) {
        console.error('Failed to fetch linkable entities:', e);
      }
    };
    fetchLinkOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/contacts/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update contact');
      }
      
      const result = await response.json();
      setContact(result.contact);
      setIsEditing(false);
      toast.success(txt.saveSuccess);
    } catch (err) {
      console.error('Error updating contact:', err);
      toast.error(txt.saveError);
    } finally {
      setSaving(false);
    }
  };

  const toggleFavorite = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/contacts/${id}/toggle-favorite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContact(prev => ({ ...prev, is_favorite: data.is_favorite }));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="contact-detail-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Card className="border-0 bg-card/50">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="contact-detail-error">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/contacts')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {txt.backToContacts}
        </Button>
        <Card className="border-0 bg-card/50">
          <CardContent className="p-12 text-center">
            <User className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">{error || txt.notFound}</h3>
            <Button variant="outline" onClick={() => navigate('/dashboard/contacts')}>
              {txt.backToContacts}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'font-arabic' : ''}`} data-testid="contact-detail-page">
      {/* WO-21 — the server masks a contact owned by someone else and marks it
          with _is_masked. Say plainly why the fields read "A**** A*****", and
          keep the Edit button away: saving this record would write the
          asterisks over the real name, email and phone. */}
      {contact._is_masked && (
        <div
          className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4"
          data-testid="contact-masked-banner"
        >
          <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            <ShieldOff className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm">
              {language === 'ar'
                ? 'جهة الاتصال هذه تعود لمستخدم آخر. تم إخفاء الاسم والبريد الإلكتروني والهاتف والملاحظات لحماية الخصوصية، ولا يمكن تعديلها من هنا.'
                : 'This contact belongs to another user. Its name, email, phone and notes are hidden for privacy, and it cannot be edited from here.'}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/contacts')}
          className={`flex items-center gap-2 w-fit ${isRTL ? 'flex-row-reverse' : ''}`}
          data-testid="back-to-contacts-btn"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          {txt.backToContacts}
        </Button>
        
        <div className="flex gap-2">
          {contact._is_masked ? null : isEditing ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => setIsEditing(false)}
                disabled={saving}
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-2" />
                {txt.cancel}
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-contact-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {txt.save}
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => setIsEditing(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="edit-contact-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              {txt.editContact}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Info Card */}
        <Card className="lg:col-span-2 border-0 bg-card/50 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <User className="w-5 h-5 text-primary" />
              {txt.contactDetails}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar and Name Header */}
            <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="relative">
                <Avatar className="w-20 h-20 ring-4 ring-primary/20">
                  <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold text-2xl">
                    {getInitials(contact.name)}
                  </AvatarFallback>
                </Avatar>
                <button 
                  onClick={toggleFavorite}
                  className={`absolute -top-1 -right-1 p-1.5 rounded-full transition-colors ${
                    contact.is_favorite ? 'bg-yellow-400 text-white' : 'bg-muted hover:bg-yellow-400/50'
                  }`}
                  data-testid="toggle-favorite-btn"
                >
                  <Star className={`w-4 h-4 ${contact.is_favorite ? 'fill-current' : ''}`} />
                </button>
              </div>
              <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                {isEditing ? (
                  <Input
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="text-2xl font-bold h-12"
                    data-testid="edit-name-input"
                  />
                ) : (
                  <h2 className="text-2xl font-bold">{contact.name}</h2>
                )}
                {/* WO-21 — created by somebody else: say who. The condition
                    Lucas attached to company-wide CRM visibility, and what
                    tells a Team member this entry is not theirs to delete. */}
                {!contact.is_own && contact.owner_name && (
                  <p className="text-sm text-muted-foreground mt-1" data-testid="contact-owner">
                    {language === 'ar' ? 'أنشأها' : 'Created by'} {contact.owner_name}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {contact.position_type && (
                    <Badge className={positionColors[contact.position_type] || 'bg-gray-500/10'}>
                      {contact.position_type}
                    </Badge>
                  )}
                  {contact.source && (
                    <Badge variant="outline" className="text-xs">
                      {contact.source === 'opportunity' ? 'From CRM' : contact.source}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Contact Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Position */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Briefcase className="w-4 h-4" />
                  {txt.position}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.position}
                    onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                    data-testid="edit-position-input"
                  />
                ) : (
                  <p className="font-medium">{contact.position || '-'}</p>
                )}
              </div>

              {/* Company */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Building className="w-4 h-4" />
                  {txt.company}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.company}
                    onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                    data-testid="edit-company-input"
                  />
                ) : (
                  <p className="font-medium">{contact.company || contact.source_lead_name || '-'}</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Mail className="w-4 h-4" />
                  {txt.email}
                </Label>
                {isEditing ? (
                  <Input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    data-testid="edit-email-input"
                  />
                ) : (
                  <p className="font-medium">
                    {contact.email ? (
                      <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                        {contact.email}
                      </a>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Phone className="w-4 h-4" />
                  {txt.phone}
                </Label>
                {isEditing ? (
                  <Input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    data-testid="edit-phone-input"
                  />
                ) : (
                  <p className="font-medium">
                    {contact.phone ? (
                      <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                        {contact.phone}
                      </a>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {txt.country}
                </Label>
                {isEditing ? (
                  <select
                    value={editData.country}
                    onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="edit-country-select"
                  >
                    <option value="">Select country</option>
                    {Object.keys(countryFlags).map(code => (
                      <option key={code} value={code}>
                        {countryFlags[code]} {countryNames[language][code]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium">
                    {contact.country ? (
                      <>
                        {countryFlags[contact.country]} {countryNames[language][contact.country]}
                      </>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {txt.city}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.city}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                    data-testid="edit-city-input"
                  />
                ) : (
                  <p className="font-medium">{contact.city || '-'}</p>
                )}
              </div>

              {/* LinkedIn */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Linkedin className="w-4 h-4" />
                  {txt.linkedin}
                </Label>
                {isEditing ? (
                  <Input
                    value={editData.linkedin}
                    onChange={(e) => setEditData({ ...editData, linkedin: e.target.value })}
                    placeholder="linkedin.com/in/username"
                    data-testid="edit-linkedin-input"
                  />
                ) : (
                  <p className="font-medium">
                    {contact.linkedin ? (
                      <a 
                        href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-2"
                      >
                        {contact.linkedin}
                        <Link2 className="w-3 h-3" />
                      </a>
                    ) : '-'}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2 md:col-span-2">
                <Label className={`flex items-center gap-2 text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="w-4 h-4" />
                  {txt.notes}
                </Label>
                {isEditing ? (
                  <textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    className="w-full h-24 px-3 py-2 rounded-md border border-input bg-background resize-none"
                    data-testid="edit-notes-input"
                  />
                ) : (
                  <p className="font-medium text-sm">{contact.notes || '-'}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timestamps Card */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-4 space-y-4">
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{txt.createdAt}:</span>
                <span className="font-medium">{formatDate(contact.created_at)}</span>
              </div>
              <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">{txt.updatedAt}:</span>
                <span className="font-medium">{formatDate(contact.updated_at)}</span>
              </div>
              {contact.last_contact && (
                <div className={`flex items-center gap-2 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{txt.lastContact}:</span>
                  <span className="font-medium">{formatDate(contact.last_contact)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-4 space-y-2">
              {contact.email && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open(`mailto:${contact.email}`, '_blank')}
                  data-testid="send-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'إرسال بريد إلكتروني' : 'Send Email'}
                </Button>
              )}
              {contact.phone && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open(`tel:${contact.phone}`, '_blank')}
                  data-testid="call-phone-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'اتصال' : 'Call'}
                </Button>
              )}
              {contact.linkedin && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open(contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`, '_blank')}
                  data-testid="view-linkedin-btn"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  {language === 'ar' ? 'عرض LinkedIn' : 'View LinkedIn'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Linked Entities */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Link2 className="w-4 h-4 text-purple-500" />
                {language === 'ar' ? 'الروابط' : 'Linked Entities'}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowLinksDialog(true)}
                data-testid="manage-links-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                {language === 'ar' ? 'إدارة' : 'Manage'}
              </Button>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> {language === 'ar' ? 'الشركات' : 'Companies'}
                </p>
                {(contact.linked_companies?.length || 0) > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {contact.linked_companies.map(c => (
                      <Badge
                        key={c.id}
                        variant="outline"
                        className="gap-1 cursor-pointer border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10"
                        onClick={() => navigate(`/dashboard/companies/${c.id}`)}
                        data-testid={`linked-company-${c.id}`}
                      >
                        {c.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لا توجد' : 'None'}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> {language === 'ar' ? 'المشاريع' : 'Projects'}
                </p>
                {(contact.linked_projects?.length || 0) > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {contact.linked_projects.map(p => (
                      <Badge
                        key={p.id}
                        variant="outline"
                        className="gap-1 cursor-pointer border-purple-500/30 text-purple-600 dark:text-purple-400 hover:bg-purple-500/10"
                        onClick={() => navigate(`/dashboard/projects/${p.id}`)}
                        data-testid={`linked-project-${p.id}`}
                      >
                        {p.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لا توجد' : 'None'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Related Opportunities */}
          <Card className="border-0 bg-card/50 backdrop-blur-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                {txt.relatedOpportunities}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {relatedOpportunities.length > 0 ? (
                <div className="space-y-2">
                  {relatedOpportunities.map((opp) => (
                    <div 
                      key={opp._id}
                      className="p-2 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate('/dashboard/crm')}
                    >
                      <p className="text-sm font-medium truncate">{opp.name}</p>
                      <p className="text-xs text-muted-foreground">{opp.status}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{txt.noRelatedItems}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Manage Links Dialog */}
      <ContactLinksDialog
        open={showLinksDialog}
        onOpenChange={setShowLinksDialog}
        contact={contact}
        companies={linkOptions.companies}
        projects={linkOptions.projects}
        token={getToken()}
        language={language}
        isRTL={isRTL}
        onSaved={() => fetchContact()}
      />
    </div>
  );
}
